import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { z } from 'zod'

import { retryTransientRead } from '../../services/api/client'
import { subscribeInvalidation } from '../../services/dashboard-invalidation'
import { validTimestampSchema } from '../conversations/conversations-contracts'
import { actionRequestTypeSchema } from '../conversations/interaction-operational-contracts'

// Outstanding work: the conversations that need action or review, counted
// once each by the same rule Conversations shows. Overview's Needs attention
// and the notification badge read this one query, so they always agree.
// Counts grow with a clinic's history, so they are bounded only for sanity.
export const historyCountSchema = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER)

export const conversationAttentionSchema = z
  .object({
    schemaVersion: z.literal(2),
    outstandingCount: historyCountSchema,
    needsActionCount: historyCountSchema,
    needsReviewCount: historyCountSchema,
    items: z
      .array(
        z
          .object({
            aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
            attention: z.enum(['NEEDS_ACTION', 'NEEDS_REVIEW']),
            activityAtMs: validTimestampSchema,
            unresolvedActionRequestCount: historyCountSchema,
            unresolvedActionRequestTypes: z
              .array(actionRequestTypeSchema)
              .max(4),
          })
          .strict()
      )
      .max(25),
  })
  .strict()
  .refine(
    (value) =>
      value.outstandingCount ===
      value.needsActionCount + value.needsReviewCount,
    'Outstanding work must be the conversations that need action or review'
  )

export type ConversationAttention = z.infer<typeof conversationAttentionSchema>
export type ConversationAttentionItem = ConversationAttention['items'][number]

export function conversationAttentionPath(tenantId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/attention?pageSize=5&version=2`
}

export function conversationAttentionQueryKey(tenantId: string | null) {
  return ['conversation-attention', tenantId] as const
}

// Attention is re-read when an aggregate, review or Action Request changes.
// Each read covers the clinic's whole history, so a burst of hints is folded
// into at most one read per gap; while hints are live the timer is only a
// safety net. Without live hints each caller's own interval applies, as before.
const HINT_MIN_GAP_MS = 30_000
const HINTED_FALLBACK_MS = 5 * 60_000
const IN_FLIGHT_RECHECK_MS = 1_000

// One pending re-read per clinic, however many views show attention.
type PendingRead = {
  users: number
  timer?: ReturnType<typeof setTimeout>
}
const pendingReads = new WeakMap<QueryClient, Map<string, PendingRead>>()

function pendingRead(queryClient: QueryClient, tenantId: string) {
  let reads = pendingReads.get(queryClient)
  if (!reads)
    pendingReads.set(queryClient, (reads = new Map<string, PendingRead>()))
  let pending = reads.get(tenantId)
  if (!pending) reads.set(tenantId, (pending = { users: 0 }))
  return { reads, pending }
}

function scheduleAttentionRead(
  queryClient: QueryClient,
  tenantId: string,
  pending: PendingRead
) {
  if (pending.timer !== undefined || pending.users === 0) return
  const queryKey = conversationAttentionQueryKey(tenantId)
  const fire = () => {
    pending.timer = undefined
    if (pending.users === 0) return
    // A hidden tab reads nothing; the hint channel reconciles on its return.
    if (document.visibilityState === 'hidden') return
    // A read already running may predate the change: re-read after it.
    if (queryClient.isFetching({ queryKey }) > 0) {
      pending.timer = setTimeout(fire, IN_FLIGHT_RECHECK_MS)
      return
    }
    void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false })
  }
  const readAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0
  pending.timer = setTimeout(
    fire,
    Math.max(0, readAt + HINT_MIN_GAP_MS - Date.now())
  )
}

export function useConversationAttention(
  load: (tenantId: string) => Promise<ConversationAttention>,
  tenantId: string | null,
  refetchInterval: number
) {
  const queryClient = useQueryClient()
  const [hintsHealthy, setHintsHealthy] = useState(false)
  const query = useQuery({
    queryKey: conversationAttentionQueryKey(tenantId),
    enabled: Boolean(tenantId),
    queryFn: () => load(tenantId as string),
    refetchInterval: hintsHealthy
      ? Math.max(refetchInterval, HINTED_FALLBACK_MS)
      : refetchInterval,
    retry: retryTransientRead,
  })
  useEffect(() => {
    setHintsHealthy(false)
    if (!tenantId) return
    const { reads, pending } = pendingRead(queryClient, tenantId)
    pending.users += 1
    // Joining an open hub reports its health at once; that is not a change,
    // and this view's own first read is current, so it schedules nothing.
    let joining = true
    const stop = subscribeInvalidation(
      tenantId,
      (healthy) => {
        setHintsHealthy(healthy)
        if (!joining) scheduleAttentionRead(queryClient, tenantId, pending)
      },
      ['aggregates', 'operational']
    )
    joining = false
    return () => {
      stop()
      pending.users -= 1
      if (pending.users === 0) {
        clearTimeout(pending.timer)
        reads.delete(tenantId)
      }
    }
  }, [queryClient, tenantId])
  return query
}
